import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import { min } from "lodash-es";
import {
  ExperienceBookableItem,
  ExperienceId,
  ExperienceOptionId,
  ExperiencePricingCategory,
  ExperiencePricingCategoryEnum,
  ExperiencesAgeBandEnum,
  ExperiencesShopOptionRequest,
  IExperiencesAgeBandCounts,
  IExperiencesPassengerCounts,
  ParsedExperienceBookableItem,
} from "redmond";
import {
  DesktopPopupModal,
  getPriceString,
  Icon,
  IconName,
  PickerType,
} from "halifax";

import styles from "./styles.module.scss";
import {
  getPaxMixFromTravelersCount,
  getTotalTravelersCount,
  MonthAndDatePicker,
  MonthAndDatePickerType,
} from "../../../common";
import { TOTAL_NUM_TRAVELERS, START_TIME, DATE } from "../../../common/textConstants";
import { ParsedAgeBand } from "../../reducer";
import {
  AgeBandCountPickerTitles,
  PassengerCountPicker,
  PassengerCountPickerTitles,
  TravelerCountPickerTitles,
} from "../ShopPassengerCountPicker/component";
import * as textConstants from "../../textConstants";
import { getMinMaxHelperText } from "../ShopOverview/component";

export interface IShopSelectionButtonsProps {
  experienceId?: ExperienceId;
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
  selectedTime: string | null;
  setSelectedTime: (time: string) => void;
  timeSelectOptions?: string[];
  travelerCounts?: IExperiencesPassengerCounts;
  setTravelerCounts: (counts: IExperiencesPassengerCounts) => void;
  parsedAgeBands?: { [key in ExperiencesAgeBandEnum]?: ParsedAgeBand };
  experienceMaxTravelers?: number;
  adultRequired?: boolean;
  selectedBookableItem: ExperienceBookableItem | null;
  bookableItems?: ParsedExperienceBookableItem[];
  pricingCategory?: ExperiencePricingCategory;
  fromDate: Date | null;
  untilDate: Date | null;
  setFromDate: (date: Date | null) => void;
  setUntilDate: (date: Date | null) => void;
  fetchExperiencesShop: () => void;
  fetchExperiencesShopOptions: (request: ExperiencesShopOptionRequest) => void;
  selectedOptionId?: ExperienceOptionId;
  pricingOpaque?: string;
}

/**
 * Desktop-only dateless shop selectors. Renders date, time, and traveler
 * card-buttons in the right checkout panel (inside ShopCheckoutBreakdown)
 * instead of the left-side ShopOverview where they normally live.
 *
 * State/effects (modalTitles, variantMax, unavailableDates, pricePerDay,
 * handleFetchShopOptions, PassengerCountPicker modal) are copied from
 * ShopOverview — same logic, different layout container.
 * The calendar modal reuses the same DesktopPopupModal + MonthAndDatePicker
 * pattern from ShopOverview's "View All" calendar.
 *
 * New UI: card-style <button> elements with a custom time dropdown,
 * replacing ShopOverview's B2BButton / MUI Select.
 */
export const ShopSelectionButtons = (props: IShopSelectionButtonsProps) => {
  const {
    experienceId,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    timeSelectOptions,
    travelerCounts,
    setTravelerCounts,
    parsedAgeBands,
    experienceMaxTravelers,
    adultRequired,
    selectedBookableItem,
    bookableItems,
    pricingCategory,
    fromDate,
    untilDate,
    setFromDate,
    setUntilDate,
    fetchExperiencesShop,
    fetchExperiencesShopOptions,
    selectedOptionId,
    pricingOpaque,
  } = props;

  const [openCalendarModal, setOpenCalendarModal] = useState(false);
  const [openTravelerCountPicker, setOpenTravelerCountPicker] = useState(false);
  const [openTimeDropdown, setOpenTimeDropdown] = useState(false);

  const [localFromDate, setLocalFromDate] = useState<Date | null>(null);
  const [localUntilDate, setLocalUntilDate] = useState<Date | null>(null);

  const [modalTitles, setModalTitles] = useState<
    PassengerCountPickerTitles | undefined
  >(undefined);
  const [totalTravelers, setTotalTravelers] = useState<number>(1);
  const [variantMax, setVariantMax] = useState<number | undefined>(
    experienceMaxTravelers
  );

  const timeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTotalTravelers(getTotalTravelersCount(travelerCounts));
  }, [travelerCounts]);

  useEffect(() => {
    setLocalFromDate(fromDate);
  }, [fromDate]);

  useEffect(() => {
    setLocalUntilDate(untilDate);
  }, [untilDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenTimeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unavailableDates = useMemo(
    () =>
      selectedBookableItem?.dailyPrices
        ?.filter((daily) => daily.price === undefined)
        .map((daily) => dayjs(daily.date).toDate()),
    [selectedBookableItem]
  );

  const pricePerDay: { [date: string]: string } = useMemo(() => {
    const priceDateMap: { [date: string]: string } = {};
    selectedBookableItem?.dailyPrices
      ?.filter((daily) => daily.price)
      .forEach((daily) => {
        const date = dayjs(daily.date).format("YYYY-MM-DD");
        const { price } = daily;
        if (price) {
          priceDateMap[date] = getPriceString({
            price: price.fiat.value,
            currencySymbol: price.fiat.currencySymbol,
          });
        }
      });
    return priceDateMap;
  }, [selectedBookableItem]);

  // Traveler modal titles + variantMax — same logic as ShopOverview and
  // ShopMobileAvailabilityCard (builds picker labels from parsedAgeBands).
  useEffect(() => {
    if (parsedAgeBands) {
      if (parsedAgeBands.Adult) {
        const newModalTitles: AgeBandCountPickerTitles = {
          modalTitle: textConstants.EDIT_TRAVELERS,
          adultTitle: textConstants.ADULTS,
          adultSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Adult?.minAge,
            parsedAgeBands.Adult?.maxAge
          ),
          adultHelperText: parsedAgeBands.Adult
            ? getMinMaxHelperText(
                parsedAgeBands.Adult.minNumberOfTravelers,
                parsedAgeBands.Adult.maxNumberOfTravelers
              )
            : undefined,
          adultPrice: parsedAgeBands.Adult
            ? textConstants.makePriceString(parsedAgeBands.Adult)
            : undefined,
          childrenPrice: parsedAgeBands.Child
            ? textConstants.makePriceString(parsedAgeBands.Child)
            : undefined,
          childrenTitle: parsedAgeBands.Child ? textConstants.CHILDREN : "",
          childrenSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Child?.minAge,
            parsedAgeBands.Child?.maxAge
          ),
          childrenHelperText: parsedAgeBands.Child
            ? getMinMaxHelperText(
                parsedAgeBands.Child.minNumberOfTravelers,
                parsedAgeBands.Child.maxNumberOfTravelers
              )
            : undefined,
          seniorTitle: parsedAgeBands.Senior
            ? textConstants.SENIORS
            : undefined,
          seniorSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Senior?.minAge,
            parsedAgeBands.Senior?.maxAge
          ),
          seniorHelperText: parsedAgeBands.Senior
            ? getMinMaxHelperText(
                parsedAgeBands.Senior.minNumberOfTravelers,
                parsedAgeBands.Senior.maxNumberOfTravelers
              )
            : undefined,
          seniorPrice: parsedAgeBands.Senior
            ? textConstants.makePriceString(parsedAgeBands.Senior)
            : undefined,
          youthTitle: parsedAgeBands.Youth ? textConstants.YOUTHS : undefined,
          youthSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Youth?.minAge,
            parsedAgeBands.Youth?.maxAge
          ),
          youthHelperText: parsedAgeBands.Youth
            ? getMinMaxHelperText(
                parsedAgeBands.Youth.minNumberOfTravelers,
                parsedAgeBands.Youth.maxNumberOfTravelers
              )
            : undefined,
          youthPrice: parsedAgeBands.Youth
            ? textConstants.makePriceString(parsedAgeBands.Youth)
            : undefined,
          infantTitle: parsedAgeBands.Infant
            ? textConstants.INFANTS
            : undefined,
          infantSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Infant?.minAge,
            parsedAgeBands.Infant?.maxAge
          ),
          infantHelperText: parsedAgeBands.Infant
            ? getMinMaxHelperText(
                parsedAgeBands.Infant.minNumberOfTravelers,
                parsedAgeBands.Infant.maxNumberOfTravelers
              )
            : undefined,
          infantPrice: parsedAgeBands.Infant
            ? textConstants.makePriceString(parsedAgeBands.Infant)
            : undefined,
        };
        setModalTitles(newModalTitles);
      } else if (parsedAgeBands.Traveler) {
        const newModalTitles: TravelerCountPickerTitles = {
          modalTitle: textConstants.EDIT_TRAVELERS,
          travelerTitle: textConstants.TRAVELER,
          travelerSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Traveler?.minAge,
            parsedAgeBands.Traveler?.maxAge
          ),
          travelerHelperText: parsedAgeBands.Traveler
            ? getMinMaxHelperText(
                parsedAgeBands.Traveler.minNumberOfTravelers,
                parsedAgeBands.Traveler.maxNumberOfTravelers
              )
            : undefined,
          travelerPrice: parsedAgeBands.Traveler
            ? textConstants.makePriceString(parsedAgeBands.Traveler)
            : undefined,
        };
        setModalTitles(newModalTitles);
      }

      let newVariantMax = 0;
      Object.values(parsedAgeBands).forEach((value) => {
        if (value.maxNumberOfTravelers !== undefined) {
          newVariantMax += value.maxNumberOfTravelers;
        } else {
          newVariantMax = experienceMaxTravelers ?? Infinity;
        }
      });
      setVariantMax(newVariantMax);
    }
  }, [parsedAgeBands, selectedBookableItem, selectedDate, travelerCounts, experienceMaxTravelers]);

  const handleFetchShopOptions = (
    travelersCount: IExperiencesPassengerCounts
  ) => {
    if (selectedOptionId && selectedDate && experienceId) {
      const selectedItem = bookableItems?.find(
        (item) => item.experienceOptionId.value === selectedOptionId.value
      );
      const timeSlotPrices = selectedItem?.timeSlotsByDate[selectedDate] || [];
      const ageBandPrices =
        selectedItem?.ageBandPricesByDate[selectedDate] || [];

      fetchExperiencesShopOptions({
        experienceId,
        experienceOptionId:
          selectedBookableItem?.experienceOptionId ?? selectedOptionId,
        paxMix: getPaxMixFromTravelersCount(travelersCount),
        date: selectedDate,
        currency: "USD",
        shopPricingOpaque: pricingOpaque,
        timeSlotPrices,
        ageBandPrices,
      });
    }
  };

  // Same refetch-on-date-change pattern as ShopMobileAvailabilityCard's
  // calendar handleSearch, adapted for desktop modal dismiss.
  const handleClickDone = () => {
    setOpenCalendarModal(false);
    const datesChanged =
      localFromDate?.getTime() !== fromDate?.getTime() ||
      localUntilDate?.getTime() !== untilDate?.getTime();
    setFromDate(localFromDate);
    setUntilDate(localUntilDate);
    if (datesChanged) {
      setSelectedDate(null);
      fetchExperiencesShop();
      window.scrollTo(0, 0);
    }
  };

  return (
    <Box className={styles["shop-selection-buttons-container"]}>
      <Box className={styles["selection-buttons-row"]}>
        {/* Date Button */}
        <button
          type="button"
          className={clsx(styles["selection-button"], styles["date-button"])}
          onClick={() => setOpenCalendarModal(true)}
        >
          <Icon
            className={styles["selection-button-icon"]}
            name={IconName.CalendarWithBoxIcon}
            ariaLabel=""
          />
          <Box className={styles["selection-button-text"]}>
            <Typography className={styles["selection-button-label"]}>
              {DATE}
            </Typography>
            <Typography className={styles["selection-button-value"]}>
              {selectedDate
                ? dayjs(selectedDate).format("ddd, MMM D")
                : textConstants.SELECT_DATE}
            </Typography>
          </Box>
        </button>

        {/* Start Time Button */}
        {selectedTime && timeSelectOptions && (
          <div
            className={clsx(
              styles["time-dropdown-container"],
              styles["start-time-button"]
            )}
            ref={timeDropdownRef}
          >
            <button
              type="button"
              className={styles["selection-button"]}
              onClick={() => setOpenTimeDropdown(!openTimeDropdown)}
            >
              <Icon
                className={styles["selection-button-icon"]}
                name="clock-icon"
                ariaLabel=""
              />
              <Box className={styles["selection-button-text"]}>
                <Typography className={styles["selection-button-label"]}>
                  {START_TIME}
                </Typography>
                <Typography className={styles["selection-button-value"]}>
                  {selectedTime}
                </Typography>
              </Box>
            </button>
            {openTimeDropdown && (
              <Box className={styles["time-dropdown-menu"]}>
                {timeSelectOptions.map((timeOption) => (
                  <button
                    type="button"
                    key={timeOption}
                    className={clsx(styles["time-dropdown-option"], {
                      [styles.selected]: timeOption === selectedTime,
                    })}
                    onClick={() => {
                      setSelectedTime(timeOption);
                      setOpenTimeDropdown(false);
                    }}
                  >
                    {timeOption}
                  </button>
                ))}
              </Box>
            )}
          </div>
        )}
      </Box>

      {/* Travelers Button */}
      <button
        type="button"
        className={clsx(
          styles["selection-button"],
          styles["travelers-button"]
        )}
        onClick={() => setOpenTravelerCountPicker(true)}
      >
        <Icon
          className={styles["selection-button-icon"]}
          name={IconName.B2BUser}
          ariaLabel=""
        />
        <Box className={styles["selection-button-text"]}>
          <Typography className={styles["selection-button-label"]}>
            {textConstants.TRAVELER}
          </Typography>
          <Typography className={styles["selection-button-value"]}>
            {TOTAL_NUM_TRAVELERS(totalTravelers)}
          </Typography>
        </Box>
      </button>

      {/* Calendar Modal */}
      <DesktopPopupModal
        open={openCalendarModal}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          setOpenCalendarModal(false);
        }}
        className={clsx(
          "desktop-calendar-picker-popup-root",
          "desktop-experiences-calendar-picker-popup-root",
          "experiences-module"
        )}
        contentClassName="desktop-calendar-picker-wrapper"
        invisibleBackdrop={false}
      >
        <MonthAndDatePicker
          pickerType={PickerType.DAY}
          viewType={MonthAndDatePickerType.Horizontal}
          startDate={localFromDate}
          endDate={localUntilDate}
          setStartDate={(val: Date | null) => {
            setLocalFromDate(val);
            setLocalUntilDate(val);
          }}
          setEndDate={() => {}}
          header={textConstants.SELECT_DATE}
          subheader={
            pricingCategory?.ExperiencePricingCategory ===
            ExperiencePricingCategoryEnum.PerGroup
              ? textConstants.PRICE_PER_GROUP
              : textConstants.PRICE_PER_ADULT
          }
          className={clsx("experiences-shop-date-picker", "b2b")}
          unavailableDates={unavailableDates}
          pricePerDay={pricePerDay}
        />
        <Button
          onClick={handleClickDone}
          disabled={!localFromDate || !localUntilDate}
          className="select-dates-button"
          variant="contained"
        >
          {textConstants.SELECT_DATE}
        </Button>
      </DesktopPopupModal>

      {/* Traveler Count Picker Modal */}
      {travelerCounts && setTravelerCounts && parsedAgeBands && modalTitles && (
        <DesktopPopupModal
          open={openTravelerCountPicker}
          className={
            styles["desktop-experiences-passenger-picker-popup-root"]
          }
          contentClassName="desktop-passenger-count-picker-popup-container"
          onClose={() => setOpenTravelerCountPicker(false)}
          invisibleBackdrop={false}
        >
          <PassengerCountPicker
            minimumAdultsCount={0}
            titles={modalTitles}
            setPassengerCounts={(counts) => {
              const travellers = counts as IExperiencesPassengerCounts;
              setTravelerCounts(travellers);
              handleFetchShopOptions(travellers);
            }}
            onClickApply={() => {
              setOpenTravelerCountPicker(false);
            }}
            counts={travelerCounts}
            className="b2b"
            maximumTravelersCount={
              min([experienceMaxTravelers, variantMax]) ?? 0
            }
            paxCountLimits={
              parsedAgeBands.Adult
                ? {
                    seniorsCount: {
                      min: parsedAgeBands?.Senior?.minNumberOfTravelers,
                      max: parsedAgeBands?.Senior?.maxNumberOfTravelers,
                    },
                    adultsCount: {
                      min:
                        parsedAgeBands?.Adult?.minNumberOfTravelers ||
                        parsedAgeBands?.Traveler?.minNumberOfTravelers,
                      max:
                        parsedAgeBands?.Adult?.maxNumberOfTravelers ||
                        parsedAgeBands?.Traveler?.maxNumberOfTravelers,
                    },
                    youthsCount: {
                      min: parsedAgeBands?.Youth?.minNumberOfTravelers,
                      max: parsedAgeBands?.Youth?.maxNumberOfTravelers,
                    },
                    childrenCount: {
                      min: parsedAgeBands?.Child?.minNumberOfTravelers,
                      max: parsedAgeBands?.Child?.maxNumberOfTravelers,
                    },
                    infantsCount: {
                      min: parsedAgeBands?.Infant?.minNumberOfTravelers,
                      max: parsedAgeBands?.Infant?.maxNumberOfTravelers,
                    },
                  }
                : {
                    travelersCount: {
                      min: parsedAgeBands?.Traveler?.minNumberOfTravelers,
                      max: parsedAgeBands?.Traveler?.maxNumberOfTravelers,
                    },
                  }
            }
            getPaxCountsError={(counts) => {
              const passengerCounts = counts as IExperiencesAgeBandCounts;
              let errorText = "";
              if (adultRequired) {
                errorText =
                  passengerCounts.adultsCount +
                    (passengerCounts.seniorsCount ?? 0) <=
                  0
                    ? textConstants.ADULT_REQUIRED
                    : "";
              }
              return errorText;
            }}
          />
        </DesktopPopupModal>
      )}
    </Box>
  );
};
